import { createContext, useContext, ReactNode, useEffect, useState, useRef } from "react";
import {
  MsalProvider,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import { msalInstance } from "@/App";
import { loginRequestScope } from "@/constants/msal";
import { AccountInfo, Configuration, InteractionStatus, IPublicClientApplication } from "@azure/msal-browser";
import { LOCAL_STORAGE } from "@/constants/commons";


interface AuthContextState {
  isAuthenticated: boolean;
  instance: IPublicClientApplication;
  inProgress: InteractionStatus;
  isLoading: boolean;
  account: AccountInfo;
  login: (scopes: string[]) => void;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextState | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro del AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children, msalConfig }: { children: ReactNode, msalConfig: Configuration }) => (
  <MsalProvider instance={msalInstance}>
    <AuthContextWrapper msalConfig={msalConfig}>{children}</AuthContextWrapper>
  </MsalProvider>
);

const AuthContextWrapper = ({ children, msalConfig }: { children: ReactNode, msalConfig: Configuration }) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();

  const [isLoading, setLoading] = useState(true);
  const initializeRef = useRef(false);
  useEffect(() => {
    if (initializeRef.current) return;
    
    const init = async () => {
      await instance.initialize();
      initializeRef.current = true;
    };

    init();
  }, [instance]);

  /**
    * Este efecto se asegura de que el estado de carga se actualice correctamente después de la inicialización y 
    * el manejo de redirecciones, evitando que el componente quede en un estado de carga indefinido. 
    * Se basa en el estado `inProgress` para determinar cuándo la autenticación ha terminado su proceso inicial, 
    * lo que es crucial para una experiencia de usuario fluida.
   */
  useEffect(() => {
    if (!initializeRef.current) return;
    if (inProgress === InteractionStatus.None || inProgress === InteractionStatus.HandleRedirect) {
      setLoading(false);
    }
  }, [inProgress]);


  const login = (scopes: string[]) => instance.loginRedirect({ scopes, redirectStartPage: import.meta.env.VITE_APP_IAM_REDIRECT_URI  });

  const logout = async () => {
    if (msalConfig.cache?.cacheLocation === LOCAL_STORAGE) {
      localStorage.clear();
    } else {
      sessionStorage.clear();
    }
    await instance.clearCache();
    await instance.logoutRedirect();

  };

  const account = accounts[0];

  const getToken = async () => {

    try {
      const result = await instance.acquireTokenSilent({
        ...{ scopes: loginRequestScope },
        account: accounts[0],
      });

      return result.accessToken;
    } catch (err) {
      instance.acquireTokenRedirect({ scopes: loginRequestScope });
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, account, login, logout, getToken, 
      instance, inProgress }}>
      {children}
    </AuthContext.Provider>
  );
};
